import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@/types';
import { ROUTES } from '@/config/routes';
import { useAuth } from '@/hooks/useAuth';
import { bellIcon, calendarIcon, sidebarIcon } from '@/assets/icons';
import { AssetIcon } from '@/components/icons';

interface TopbarProps {
  user: User;
  onSidebarToggle: () => void;
  sidebarOpen?: boolean;
}

export function Topbar({ user, onSidebarToggle, sidebarOpen = true }: TopbarProps) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = useCallback(() => {
    setProfileMenuOpen(false);
    logout();
    navigate(ROUTES.LOGIN, { replace: true });
  }, [logout, navigate]);

  useEffect(() => {
    if (!profileMenuOpen) return;

    const onMouseDown = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setProfileMenuOpen(false);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [profileMenuOpen]);

  return (
    <header className="topbar">
      <div className="topbar__left">
        {sidebarOpen ? (
          <button className="topbar__sidebar-toggle" onClick={onSidebarToggle} aria-label="Collapse sidebar">
            <AssetIcon src={sidebarIcon} size={20} className="topbar__sidebar-toggle-icon" />
          </button>
        ) : null}
      </div>

      <div className="topbar__right">
        <button className="topbar__icon-btn" aria-label="Calendar">
          <AssetIcon src={calendarIcon} width={24} height={24} className="topbar__icon-btn-glyph" />
        </button>

        <button className="topbar__icon-btn topbar__icon-btn--bell" aria-label="Notifications">
          <AssetIcon src={bellIcon} width={24} height={24} className="topbar__icon-btn-glyph" />
          <span className="topbar__badge">1</span>
        </button>

        <div className="topbar__user-wrap" ref={profileMenuRef}>
          <button
            type="button"
            className={`topbar__user${profileMenuOpen ? ' topbar__user--open' : ''}`}
            aria-haspopup="menu"
            aria-expanded={profileMenuOpen}
            aria-label="User profile menu"
            onClick={() => setProfileMenuOpen((open) => !open)}
          >
            <div className="topbar__avatar">{user.initials ?? 'VK'}</div>
            <div className="topbar__user-info">
              <span className="topbar__user-name">{user.name}</span>
              <span className="topbar__user-role">Admin</span>
            </div>
            <svg className="topbar__chevron" width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden>
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {profileMenuOpen && (
            <div className="topbar__user-menu" role="menu">
              <button type="button" className="topbar__user-menu-item" role="menuitem" onClick={handleLogout}>
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
